import type { DataSourceDataParams } from "@infinite-table/infinite-react";
import { infiniteQueryOptions, keepPreviousData } from "@tanstack/react-query";
import type {
	FilterParams,
	LookupParams,
	PaginationParams,
	ResponseReadAll,
} from "epsm-api-types";
import type { News } from "epsm-api-types/models";
import { getParams } from "@/lib/utils";
import newsConfig from "../../config";
import {
	type NEWS_CATEGORY,
	NEWS_COLUMNS,
	type NEWS_DATASET,
	type NEWS_SECTOR,
	type TICKER_FILTER,
} from "../../types";
import { getNewsFilterByCategory } from "../helpers/getNewsFilterByCategory";
import { getNewsFilterByDataset } from "../helpers/getNewsFilterByDataset";
import { getNewsFilterBySector } from "../helpers/getNewsFilterBySector";
import { getNewsFilterByTicker } from "../helpers/getNewsFilterByTicker";
import { getTransformedFilterByEstimate } from "../helpers/transformFiltersForEstimate";
import type { Options } from "./UseData";

const newsApi = {
	totalCount: 0,
	DEFAULT_PAGE_SIZE: newsConfig.DEFAULT_PAGE_SIZE || 0,

	getNews: async function (
		params: DataSourceDataParams<News>,
		execute: (
			options: Readonly<Options>,
		) => Promise<ResponseReadAll<News> | null>,
		lookup: LookupParams | undefined,
		columnOrder: string[],
		pagination: PaginationParams,
		filter?: FilterParams,
		signal?: AbortSignal,
	) {
		const result = await execute({
			params: getParams(params, columnOrder, pagination.limit, filter, lookup),
			signal,
		});

		if (result?.metadata?.total) {
			this.totalCount = result.metadata.total;
		}

		return {
			data: result?.data || [],
			hasMore: result?.metadata?.pagination?.nextPage,
			totalCount: this.totalCount,
		};
	},

	getNewsInfinityQueryOptions: function (
		ticker: string,
		columnOrder: string[],
		pagination: PaginationParams,
		dataParams: Partial<DataSourceDataParams<News>>,
		execute: (
			options: Readonly<Options>,
		) => Promise<ResponseReadAll<News> | null>,
		lookup: LookupParams | undefined,
		tickerFilter?: TICKER_FILTER,
		filter?: FilterParams,
		datasetFilters?: NEWS_DATASET[],
		categoryFilters?: NEWS_CATEGORY[],
		sectorFilters?: NEWS_SECTOR[],
	) {
		const newsTickerFilter = getNewsFilterByTicker(ticker);
		const newsDatasetFilter = getNewsFilterByDataset(datasetFilters);
		const newsCategoryFilter = getNewsFilterByCategory(categoryFilters);
		const newsSectorFilter = getNewsFilterBySector(sectorFilters);

		const estimateFilter = getTransformedFilterByEstimate(
			dataParams?.filterValue,
		);

		const combinedFilters: FilterParams = [
			...newsTickerFilter,
			...newsDatasetFilter,
			...newsCategoryFilter,
			...newsSectorFilter,
			...estimateFilter,
			...(filter ?? []),
		];

		return infiniteQueryOptions({
			queryKey: [
				ticker,
				tickerFilter,
				datasetFilters,
				categoryFilters,
				sectorFilters,
				dataParams.groupBy,
				dataParams.sortInfo,
				dataParams.filterValue,
				lookup,
				columnOrder,
				pagination,
			],
			queryFn: async ({ pageParam = 0, signal }) => {
				const updatedFilterValue = dataParams?.filterValue?.filter(
					(f) => f.field !== NEWS_COLUMNS.SENTIMENT,
				);

				const params: DataSourceDataParams<News> = {
					originalDataArray: [],
					livePaginationCursor: pageParam,
					groupBy: dataParams.groupBy,
					sortInfo: dataParams.sortInfo,
					filterValue: updatedFilterValue,
				};

				return await this.getNews(
					params,
					execute,
					lookup,
					columnOrder,
					pagination,
					combinedFilters,
					signal,
				);
			},
			getNextPageParam: (lastPage, allPages) => {
				if (lastPage?.hasMore) {
					return (allPages?.length ?? 0) * this.DEFAULT_PAGE_SIZE;
				}
				return undefined;
			},
			select: (data) => {
				const flatData = data.pages.flatMap((x) => x.data);

				const result = {
					pages: flatData,
				};

				return result;
			},
			initialPageParam: 0,
			placeholderData: keepPreviousData,
		});
	},
};

export default newsApi;
